import React from 'react';

import { DragDropProvider, Droppable, Draggable } from '../dnd';
import { Box, Flex, Text } from 'rebass';
import { updateNestedNodes, recursivelyGetChildren } from '../dnd/helpers';

import colors from './colors';

const Swatch = ({ color, index, ...props }) => (
  <Box
    css={`
      width: 20px;
      height: 20px;
      background: ${color};
      border-radius: ${index % 2 === 0 ? '50%' : '2px'};
    `}
    {...props}
  />
);

const Card = React.forwardRef(({ isDragging, color, label, children, index, ...props}, ref) => (
  <Box
    ref={ref}
    py={1}
    {...props}
  >
    <Box
      p={3}
      css={`
        border: 1px solid ${isDragging ? 'var(--hoveringHighlightBorder)' : 'var(--border)'};
        border-radius: 8px;
        box-shadow: 0 0 10px rgba(0,0,0,0.08);
        background: var(--card);
        text-align: left;
      `}
    >
      <Flex alignItems="center">
        <Swatch color={color} mr={2} index={index} />
        {label}
      </Flex>
      {children ? (
        <Box mt={2}>
          <Text fontSize="10px" mb={1}>Nested items</Text>
          {children}
        </Box>
      ) : null}
    </Box>
  </Box>
));

const Well = React.forwardRef((props, ref) => (
  <Flex
    flexDirection="column"
    ref={ref}
    p={3}
    css={`
      /* gap: 10px; */
      width: 100%;
      min-height: 30px;
      border-radius: 8px;
      background: var(--background-alt);
    `}
    {...props}
  />
));

// nodeList but an object
const nodeList = {
  salmon: {
    label: 'Salmon',
    color: '#ff5a5f',
    parent: 'blue',
    index: 0,
  },
  teal: {
    label: 'Teal',
    color: '#008080',
    parent: 'column1',
    index: 1,
    children: [],
  },
  orange: {
    label: 'Orange',
    color: '#FFA500',
    parent: 'teal',
    index: 0,
  },
  yellow: {
    label: 'Yellow',
    color: '#FFC107',
    parent: 'column1',
    index: 0,
  },
  lime: {
    label: 'Lime',
    color: '#00FF00',
    parent: 'column1',
    index: 2,
  },
  blue: {
    label: 'Blue',
    color: '#00BFFF',
    parent: 'column2',
    index: 0,
    children: [],
  },
  green: {
    label: 'Green',
    color: '#329E54',
    parent: 'blue',
    index: 0,
  },
  purple: {
    label: 'Purple',
    color: '#9C27B0',
    parent: 'column2',
    index: 0,
  },
  clay: {
    label: 'Clay',
    color: '#B0432B',
    parent: 'column2',
    index: 0,
  }
};

const ColorList = ({ showDebug, shouldAnimate }) => {
  const [nodes, setNodes] = React.useState(nodeList);

  const handleDragEnd = ({ id, from, to }) => {
    setNodes(n => {
      return updateNestedNodes(n, id, from ,to);
    });
  };

  function makeList(column) {
    return column.map(({ id, color, label, children }, index) => (
      <Draggable key={id} draggableId={id} index={index}>
        {(provided, snapshot) => (
          <Card
            index={index}
            isDragging={snapshot.isDragging}
            label={label}
            color={color}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >  
            {children && (
              <Droppable droppableId={id}>
                {(provided, snapshot) => (
                  <Well
                    mr={4}
                    ref={provided.innerRef}
                    style={{
                      minHeight: '100px',
                      background: snapshot.isDraggingOver ? 'var(--hoveringHighlight)' : undefined,
                    }}
                    {...provided.droppableProps}
                  >
                    {makeList(children)}
                  </Well>
                )}
              </Droppable>
            )}
          </Card>
        )}
      </Draggable>
    ));
  }

  return (
    <DragDropProvider
      onDragEnd={handleDragEnd}
      showDebug={showDebug}
      shouldAnimate={shouldAnimate}
    >
      {() => (
        <Box
          css={`
            display: flex; 
            align-items: flex-start;
            width: 750px;
            margin: 0 auto;
            & > * {
              width: 50%;
              margin: 0 20px;
            }
          `}
        >
          <Droppable droppableId="column1">
            {(provided, snapshot) => (
              <Well
                mr={4}
                ref={provided.innerRef}
                style={{
                  background: snapshot.isDraggingOver ? 'var(--hoveringHighlight)' : undefined,
                  minHeight: '100px',
                }}
                {...provided.droppableProps}
              >
                {makeList(recursivelyGetChildren(nodes, 'column1'))}
              </Well>
            )}
          </Droppable>
          <Droppable droppableId="column2">
            {(provided, snapshot) => (
              <Well
                mr={4}
                ref={provided.innerRef}
                style={{
                  background: snapshot.isDraggingOver ? 'var(--hoveringHighlight)' : undefined,
                  minHeight: '100px',
                }}
                {...provided.droppableProps}
              >
                {makeList(recursivelyGetChildren(nodes, 'column2'))}
              </Well>
            )}
          </Droppable>
        </Box>
      )}
      </DragDropProvider>
  );
};

export default ColorList;
